/**
 * Custom shaders: `.frag` files in the project that objects use as a filter
 * (`{ "type": "shader", "shader": "shaders/water.frag" }`). A shader file is a
 * GLSL ES 1.0 fragment shader. The game adds the declarations every shader
 * gets (below), so a file can be as short as its own uniforms and `main`.
 *
 * The editor makes a field for each uniform the file declares. An optional
 * comment after a uniform sets its default and how the field looks:
 *
 * ```glsl
 * uniform float uSpeed;      // 1 range 0 5        a slider
 * uniform vec3 uTint;        // color #bfe8ff      a colour swatch
 * uniform sampler2D uNoise;  // texture noise_soft the starting texture
 * uniform bool uFlip;        // true
 * uniform vec2 uCentre;      // 0.5 0.5
 * ```
 */

/** File ending of shader files. */
export const SHADER_EXTENSION = '.frag';

/** True for a project path that names a shader file. */
export function isShaderPath(path: string): boolean {
  return path.toLowerCase().endsWith(SHADER_EXTENSION);
}

/** The uniform types a shader's fields can set. */
export type ShaderUniformType = 'float' | 'int' | 'bool' | 'vec2' | 'vec3' | 'vec4' | 'sampler2D';

/** A value a scene saves for a uniform: a number (float, int, or an RGB colour), a bool, a texture key, or a vector. */
export type ShaderUniformValue = number | boolean | string | number[];

/** Uniforms the game sets on every shader. A file may declare them, or leave them out and use them anyway. */
export const SHADER_BUILTINS = {
  /** The object's picture (filters run on what the object drew). */
  uMainSampler: 'sampler2D',
  /** Seconds since the scene started. */
  uTime: 'float',
  /** Size of the picture in pixels. */
  uResolution: 'vec2',
} as const satisfies Record<string, ShaderUniformType>;

/** The varying every shader gets: where this pixel is, from 0 to 1 across the picture. */
export const SHADER_TEXCOORD = 'outTexCoord';

/** A uniform a shader file declares, read from its text, with the field the editor makes for it. */
export interface ShaderUniform {
  name: string;
  type: ShaderUniformType;
  /** What the field says: the name without its `u` prefix, with spaces between words (`uRippleCount` is "Ripple count"). */
  label: string;
  /** From the comment, or zero, false, white, or no texture. Colours are RGB numbers. */
  default: ShaderUniformValue | undefined;
  /** A slider's ends, from `range a b`. */
  range?: [number, number];
  /** A vec3 or vec4 shown as a colour, from `color`. */
  color?: boolean;
  /** The 1-based line it is declared on. */
  line: number;
}

const UNIFORM = /^\s*uniform\s+(?:(?:lowp|mediump|highp)\s+)?(float|int|bool|vec2|vec3|vec4|sampler2D)\s+([A-Za-z_]\w*)\s*;\s*(?:\/\/(.*))?$/;

/** "uRippleCount" → "Ripple count". */
export function uniformLabel(name: string): string {
  const bare = /^u[A-Z]/.test(name) ? name.slice(1) : name;
  const words = bare
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .split(/\s+/);
  const text = words.map((w, i) => (i === 0 ? w : w.toLowerCase())).join(' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function parseColor(word: string | undefined): number | undefined {
  if (!word) return undefined;
  const hex = /^#?([0-9a-f]{6})$/i.exec(word);
  return hex ? parseInt(hex[1]!, 16) : undefined;
}

function defaultFor(type: ShaderUniformType, color: boolean): ShaderUniformValue | undefined {
  switch (type) {
    case 'float':
    case 'int':
      return 0;
    case 'bool':
      return false;
    case 'vec2':
      return [0, 0];
    case 'vec3':
      return color ? 0xffffff : [0, 0, 0];
    case 'vec4':
      return color ? 0xffffff : [0, 0, 0, 0];
    case 'sampler2D':
      return undefined;
  }
}

/** Reads the comment after a uniform: its default value, then `range a b`, `color` or `texture key` in any order. */
function readHints(type: ShaderUniformType, comment: string): Pick<ShaderUniform, 'default' | 'range' | 'color'> {
  const words = comment.trim().split(/[\s,]+/).filter(Boolean);
  const out: Pick<ShaderUniform, 'default' | 'range' | 'color'> = { default: undefined };
  const numbers: number[] = [];
  for (let i = 0; i < words.length; i++) {
    const w = words[i]!;
    const lower = w.toLowerCase();
    if (lower === 'range') {
      const a = Number(words[i + 1]);
      const b = Number(words[i + 2]);
      if (Number.isFinite(a) && Number.isFinite(b)) out.range = [Math.min(a, b), Math.max(a, b)];
      i += 2;
    } else if ((lower === 'color' || lower === 'colour') && (type === 'vec3' || type === 'vec4')) {
      out.color = true;
      const c = parseColor(words[i + 1]);
      if (c !== undefined) {
        out.default = c;
        i++;
      }
    } else if (lower === 'texture' && type === 'sampler2D') {
      if (words[i + 1]) out.default = words[++i];
    } else if (type === 'bool' && (lower === 'true' || lower === 'false')) {
      out.default = lower === 'true';
    } else if (Number.isFinite(Number(w)) && w !== '') {
      numbers.push(Number(w));
    } else if (parseColor(w) !== undefined && (type === 'vec3' || type === 'vec4')) {
      out.color = true;
      out.default = parseColor(w);
    } else {
      // The rest is a description.
      break;
    }
  }
  if (out.default === undefined && numbers.length) {
    const size = { float: 1, int: 1, bool: 1, vec2: 2, vec3: 3, vec4: 4, sampler2D: 0 }[type];
    if (size === 1) out.default = type === 'int' ? Math.round(numbers[0]!) : type === 'bool' ? numbers[0] !== 0 : numbers[0];
    else if (size > 1) out.default = Array.from({ length: size }, (_, i) => numbers[i] ?? numbers[numbers.length - 1] ?? 0);
  }
  if (out.default === undefined) out.default = defaultFor(type, !!out.color);
  return out;
}

/**
 * The uniforms a shader file declares, in order, without the ones the game
 * sets itself (`uMainSampler`, `uTime`, `uResolution`).
 */
export function readShaderUniforms(source: string): ShaderUniform[] {
  const out: ShaderUniform[] = [];
  const seen = new Set<string>();
  const lines = source.split(/\r?\n/);
  let inBlock = false;
  lines.forEach((text, i) => {
    // Skip block comments, so a commented-out uniform makes no field.
    if (inBlock) {
      const end = text.indexOf('*/');
      if (end < 0) return;
      inBlock = false;
      text = text.slice(end + 2);
    }
    const start = text.indexOf('/*');
    if (start >= 0 && !text.slice(0, start).includes('//')) {
      if (text.indexOf('*/', start) < 0) inBlock = true;
      text = text.slice(0, start);
    }
    const m = UNIFORM.exec(text);
    if (!m) return;
    const type = m[1] as ShaderUniformType;
    const name = m[2]!;
    if (name in SHADER_BUILTINS || seen.has(name)) return;
    seen.add(name);
    out.push({ name, type, label: uniformLabel(name), line: i + 1, ...readHints(type, m[3] ?? '') });
  });
  return out;
}

/** A uniform's value as a scene saves it: the value set, or the shader's default. */
export function uniformValue(u: ShaderUniform, values: Record<string, unknown> | undefined): ShaderUniformValue | undefined {
  const v = values?.[u.name];
  return v === undefined ? u.default : (v as ShaderUniformValue);
}

/** Why a saved value doesn't suit its uniform, or undefined when it does. */
export function uniformValueProblem(u: ShaderUniform, value: unknown): string | undefined {
  const size = { vec2: 2, vec3: 3, vec4: 4 } as Record<string, number>;
  switch (u.type) {
    case 'float':
    case 'int':
      return typeof value === 'number' ? undefined : 'a number';
    case 'bool':
      return typeof value === 'boolean' ? undefined : 'true or false';
    case 'sampler2D':
      return typeof value === 'string' ? undefined : 'a texture key';
    default: {
      const n = size[u.type]!;
      if (Array.isArray(value) && value.length === n && value.every((x) => typeof x === 'number')) return undefined;
      if (n >= 3 && typeof value === 'number') return undefined;
      return n >= 3 ? `${n} numbers or an RGB colour` : `${n} numbers`;
    }
  }
}
